import { ProcessVersionGeneral } from 'api/endpoints/processVersions';
import { TenantGeneral } from 'api/endpoints/tenants';
import {
  WithReleaseOrDraftProcessId,
  WithTenantId,
} from 'api/types/pathParams';

import { API_REQ_ACTION_TYPES } from '../actions';
import apiReqSagaCreator from '../apiReqSagaCreator';
import { ApiPaths, HTTP_METHODS, RESPONSE_TYPE } from '../constants';

export interface CreateProcessParams {
  name: string;
  tenantId: string;
  description?: string;
}

type ProcessState = 'draft' | 'released';

export type ProcessStatus = 'active' | 'inactive';

export interface ProcessGeneral extends BaseEntity, WithHypermedia {
  description?: string;
  state: ProcessState;
  status: ProcessStatus;
  version?: ProcessVersionGeneral;
  versions?: ProcessVersionGeneral[];
}

export interface ProcessDetails extends ProcessGeneral {
  tenant: TenantGeneral;
  urls?: {
    printRender: string;
    ftpPolling: string;
    ftpError: string;
    barcodeImporter: string;
    driverSettlements: string;
  };
}

export interface ProcessConfig extends BaseEntity, WithHypermedia {
  securityGroupsEnabled: boolean;
}

export type GetProcessesParams = Pagination & {
  processStatus?: ProcessStatus;
  state?: ProcessState;
  versionStatus?: ProcessStatus;
};

export type GetProcessesResult = ProcessGeneral;

type GetByIdParams = WithReleaseOrDraftProcessId;
type GetByIdResult = ProcessDetails;
export type UpdateProcessParams = Omit<CreateProcessParams, 'tenantId'> & {
  tenant: string;
};
export type DeactivateProcessParams = WithReleaseOrDraftProcessId;
export type DeactivateProcessResult = unknown;
type ActivateProcessParams = WithReleaseOrDraftProcessId;
type ActivateProcessResult = unknown;
type ExportProcessParams = WithReleaseOrDraftProcessId;
type ExportProcessResult = Blob;
export type ImportProcessParams = { file: FormData } & WithTenantId;
export type ImportProcessResult = ProcessGeneral;

const Processes = {
  getAll: apiReqSagaCreator<GetProcessesParams, GetProcessesResult>(
    API_REQ_ACTION_TYPES.PROCESSES.GET_ALL,
    (params) => ({
      method: HTTP_METHODS.GET,
      url: ApiPaths.processes._(),
      params,
    })
  ),
  getById: apiReqSagaCreator<GetByIdParams, GetByIdResult>(
    API_REQ_ACTION_TYPES.PROCESSES.GET_BY_ID,
    (params) => ({
      method: HTTP_METHODS.GET,
      url: ApiPaths.processes.byId._(params),
    })
  ),
  deactivateProcess: apiReqSagaCreator<
    DeactivateProcessParams,
    DeactivateProcessResult
  >(API_REQ_ACTION_TYPES.PROCESSES.DEACTIVATE, (params) => ({
    method: HTTP_METHODS.POST,
    url: ApiPaths.processes.byId.deactivate._(params),
  })),
  activateProcess: apiReqSagaCreator<
    ActivateProcessParams,
    ActivateProcessResult
  >(API_REQ_ACTION_TYPES.PROCESSES.ACTIVATE, (params) => ({
    method: HTTP_METHODS.PUT,
    url: ApiPaths.processes.byId.activate(params),
  })),
  exportProcess: apiReqSagaCreator<ExportProcessParams, ExportProcessResult>(
    API_REQ_ACTION_TYPES.PROCESSES.EXPORT,
    (params) => ({
      method: HTTP_METHODS.GET,
      url: ApiPaths.processes.byId.export._(params),
      responseType: RESPONSE_TYPE.BLOB,
      transformResponse: (data) => data,
    })
  ),
};

export default Processes;
